import React, { useEffect, useState, useCallback, useMemo } from "react";
import { http, currency } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Loader2, TrendingUp, ShoppingBag, Package, DollarSign, CalendarDays, RefreshCw, Wallet, Landmark, Banknote, CreditCard, AlertCircle, FileDown, FileText } from "lucide-react";
import { es } from "date-fns/locale";
import { useSettings } from "../../lib/SettingsContext";
import { exportPaymentsCsv, exportPaymentsPdf } from "../../lib/paymentsReport";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const PERIODS = [
  { value: "day", label: "Diario", days: 30 },
  { value: "week", label: "Semanal", days: 90 },
  { value: "month", label: "Mensual", days: 365 },
];

export default function AdminMetrics() {
  const { settings } = useSettings();
  const [period, setPeriod] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Independent daily series for the calendar heatmap (last 180 days)
  const [daily, setDaily] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [calMonth, setCalMonth] = useState(new Date());
  // Daily close (cierre de corte)
  const [closeDate, setCloseDate] = useState(new Date());
  const [closeData, setCloseData] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  // Payments report (exportable)
  const today0 = new Date();
  const [reportRange, setReportRange] = useState({ from: today0, to: today0 });
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportExporting, setReportExporting] = useState(false);

  const load = useCallback(() => {
    const p = PERIODS.find((x) => x.value === period);
    setLoading(true);
    http
      .get("/admin/metrics/sales", { params: { period, days: p.days } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Fetch a longer daily window once for the calendar heatmap.
  useEffect(() => {
    http
      .get("/admin/metrics/sales", { params: { period: "day", days: 180 } })
      .then((r) => setDaily(r.data?.series || []))
      .catch(() => {});
  }, []);

  // Map YYYY-MM-DD -> {revenue, orders, units}
  const dailyMap = useMemo(() => {
    const m = {};
    for (const row of daily) m[row.bucket] = row;
    return m;
  }, [daily]);

  const maxRevenue = useMemo(
    () => daily.reduce((max, r) => Math.max(max, r.revenue || 0), 0),
    [daily]
  );

  // Format Date -> local YYYY-MM-DD (avoid UTC drift from toISOString)
  const fmtLocal = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // Fetch daily-close for the selected date
  const loadDailyClose = useCallback((d) => {
    const dateStr = fmtLocal(d);
    setCloseLoading(true);
    http
      .get("/admin/metrics/daily-close", { params: { date: dateStr } })
      .then((r) => setCloseData(r.data))
      .catch(() => setCloseData(null))
      .finally(() => setCloseLoading(false));
  }, []);

  useEffect(() => {
    loadDailyClose(closeDate);
  }, [closeDate, loadDailyClose]);

  // Fetch payments report for the selected range
  const loadReport = useCallback((range) => {
    if (!range?.from) return;
    const fromStr = fmtLocal(range.from);
    const toStr = fmtLocal(range.to || range.from);
    setReportLoading(true);
    http
      .get("/admin/metrics/payments-report", { params: { from_date: fromStr, to_date: toStr } })
      .then((r) => setReportData(r.data))
      .catch(() => setReportData(null))
      .finally(() => setReportLoading(false));
  }, []);

  useEffect(() => {
    loadReport(reportRange);
  }, [reportRange, loadReport]);

  const applyReportPreset = (kind) => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    if (kind === "today") setReportRange({ from: t, to: t });
    else if (kind === "yesterday") {
      const y = new Date(t); y.setDate(y.getDate() - 1);
      setReportRange({ from: y, to: y });
    } else if (kind === "last7") {
      const from = new Date(t); from.setDate(from.getDate() - 6);
      setReportRange({ from, to: t });
    } else if (kind === "last30") {
      const from = new Date(t); from.setDate(from.getDate() - 29);
      setReportRange({ from, to: t });
    } else if (kind === "month") {
      const from = new Date(t.getFullYear(), t.getMonth(), 1);
      setReportRange({ from, to: t });
    } else if (kind === "prevMonth") {
      const from = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const to = new Date(t.getFullYear(), t.getMonth(), 0);
      setReportRange({ from, to });
    }
  };

  const doExportCsv = () => {
    if (!reportData) return;
    setReportExporting(true);
    try {
      exportPaymentsCsv(reportData, settings?.brand_name || "Printec");
    } finally {
      setReportExporting(false);
    }
  };

  const doExportPdf = () => {
    if (!reportData) return;
    setReportExporting(true);
    try {
      exportPaymentsPdf(reportData, settings);
    } finally {
      setReportExporting(false);
    }
  };

  const selectedKey = fmtLocal(selectedDay);
  const selectedStats = dailyMap[selectedKey] || { revenue: 0, orders: 0, units: 0, paid_orders: 0 };

  // Heatmap tier buckets — split max revenue in 4 tiers
  const tierFor = (rev) => {
    if (!rev || maxRevenue === 0) return 0;
    const ratio = rev / maxRevenue;
    if (ratio > 0.75) return 4;
    if (ratio > 0.5) return 3;
    if (ratio > 0.25) return 2;
    return 1;
  };

  // Build modifiers: t1..t4 arrays of Date objects
  const heatmapModifiers = useMemo(() => {
    const buckets = { t1: [], t2: [], t3: [], t4: [] };
    for (const row of daily) {
      const t = tierFor(row.revenue);
      if (!t) continue;
      // Parse YYYY-MM-DD as local midnight
      const [y, m, d] = row.bucket.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      buckets[`t${t}`].push(dt);
    }
    return buckets;
  }, [daily, maxRevenue]);

  const totals = data?.totals || { orders: 0, revenue: 0, units: 0 };

  return (
    <div className="space-y-6" data-testid="admin-metrics">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500">/admin/métricas</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-1">Métricas de ventas</h2>
        </div>
        <div className="flex gap-1 border border-neutral-200 bg-white p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-4 py-2 text-xs font-medium transition-colors ${
                period === p.value ? "bg-black text-white" : "hover:bg-neutral-100"
              }`}
              data-testid={`metrics-period-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Ingresos", value: currency(totals.revenue), icon: DollarSign, color: "#10B981" },
          { label: "Pedidos", value: totals.orders, icon: ShoppingBag, color: "#3B82F6" },
          { label: "Piezas vendidas", value: totals.units, icon: Package, color: "#F59E0B" },
          {
            label: "Ticket promedio",
            value: totals.orders ? currency(totals.revenue / totals.orders) : currency(0),
            icon: TrendingUp,
            color: "#8B5CF6",
          },
        ].map((k) => (
          <div key={k.label} className="brutal-card p-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-neutral-500">{k.label}</div>
              <k.icon className="w-4 h-4" style={{ color: k.color }} />
            </div>
            <div className="font-display text-3xl mt-2 tracking-tight">{k.value}</div>
          </div>
        ))}
      </div>

      {/* Sales calendar (daily heatmap) */}
      <div className="brutal-card p-6" data-testid="metrics-sales-calendar">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5" />
          <h3 className="font-display text-xl tracking-tight">Ventas por día</h3>
        </div>
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div>
            <Calendar
              mode="single"
              locale={es}
              selected={selectedDay}
              onSelect={(d) => d && setSelectedDay(d)}
              month={calMonth}
              onMonthChange={setCalMonth}
              modifiers={heatmapModifiers}
              modifiersClassNames={{
                t1: "bg-emerald-100 text-emerald-900",
                t2: "bg-emerald-300 text-emerald-950",
                t3: "bg-emerald-500 text-white",
                t4: "bg-emerald-700 text-white",
              }}
              disabled={{ after: new Date() }}
              className="border border-neutral-200 bg-white"
              data-testid="metrics-calendar"
            />
            {/* Legend */}
            <div className="flex items-center gap-2 mt-3 text-[11px] font-mono text-neutral-500">
              <span>Menos</span>
              <span className="w-4 h-4 bg-neutral-100 border border-neutral-200" />
              <span className="w-4 h-4 bg-emerald-100" />
              <span className="w-4 h-4 bg-emerald-300" />
              <span className="w-4 h-4 bg-emerald-500" />
              <span className="w-4 h-4 bg-emerald-700" />
              <span>Más</span>
            </div>
          </div>
          <div className="space-y-3" data-testid="metrics-day-summary">
            <div className="text-xs font-mono uppercase text-neutral-500">Detalle del día seleccionado</div>
            <div className="font-display text-2xl tracking-tight">
              {selectedDay.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Ingresos</div>
                <div className="font-display text-2xl mt-1 tracking-tight text-emerald-700" data-testid="metrics-day-revenue">
                  {currency(selectedStats.revenue || 0)}
                </div>
              </div>
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Pedidos</div>
                <div className="font-display text-2xl mt-1 tracking-tight" data-testid="metrics-day-orders">
                  {selectedStats.orders || 0}
                </div>
              </div>
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Piezas</div>
                <div className="font-display text-2xl mt-1 tracking-tight">
                  {selectedStats.units || 0}
                </div>
              </div>
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Pagados</div>
                <div className="font-display text-2xl mt-1 tracking-tight">
                  {selectedStats.paid_orders || 0}
                </div>
              </div>
            </div>
            {selectedStats.orders === 0 && (
              <div className="text-xs text-neutral-500 italic">Este día no registra ventas. Los días con color en el calendario tienen pedidos.</div>
            )}
            <div className="text-[11px] text-neutral-500 font-mono pt-2 border-t border-neutral-100">
              Rango del calendario: últimos 180 días. Da click en cualquier día resaltado para ver el detalle.
            </div>
          </div>
        </div>
      </div>

      {/* Cierre de corte del día */}
      <div className="brutal-card p-6" data-testid="metrics-daily-close">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" />
            <h3 className="font-display text-xl tracking-tight">Cierre de corte del día</h3>
          </div>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-2 border-2 border-neutral-300 hover:bg-neutral-50 text-sm font-medium"
                  data-testid="close-date-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                  {closeDate.toLocaleDateString("es-MX", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="end">
                <div className="flex flex-wrap gap-1 p-2 border-b border-neutral-200 bg-neutral-50">
                  <button
                    type="button"
                    onClick={() => setCloseDate(new Date())}
                    className="px-2 py-1 text-[11px] font-mono uppercase border border-neutral-200 bg-white hover:bg-black hover:text-white hover:border-black transition-colors"
                    data-testid="close-preset-today"
                  >
                    Hoy
                  </button>
                  <button
                    type="button"
                    onClick={() => { const d = new Date(); d.setDate(d.getDate() - 1); setCloseDate(d); }}
                    className="px-2 py-1 text-[11px] font-mono uppercase border border-neutral-200 bg-white hover:bg-black hover:text-white hover:border-black transition-colors"
                    data-testid="close-preset-yesterday"
                  >
                    Ayer
                  </button>
                </div>
                <Calendar
                  mode="single"
                  locale={es}
                  selected={closeDate}
                  onSelect={(d) => d && setCloseDate(d)}
                  disabled={{ after: new Date() }}
                  data-testid="close-date-calendar"
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              className="rounded-none h-9"
              onClick={() => loadDailyClose(closeDate)}
              disabled={closeLoading}
              data-testid="close-refresh-btn"
              title="Refrescar (usa esto después de marcar anticipos/finiquitos en Pedidos)"
            >
              <RefreshCw className={`w-4 h-4 ${closeLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* 4 breakdown cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* 1. Pedidos del día */}
          <div className="border-2 border-black p-4 bg-white" data-testid="close-orders-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-neutral-500">Pedidos realizados</div>
              <ShoppingBag className="w-4 h-4 text-black" />
            </div>
            <div className="font-display text-4xl mt-3 tracking-tight">
              {closeData?.orders_today.count ?? 0}
            </div>
            <div className="text-xs text-neutral-600 mt-1 font-mono">
              {currency(closeData?.orders_today.revenue || 0)} facturados
            </div>
          </div>

          {/* 2. Sin anticipo */}
          <div className="border-2 border-amber-500 p-4 bg-amber-50/40" data-testid="close-pending-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-amber-700">Sin anticipo</div>
              <AlertCircle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="font-display text-4xl mt-3 tracking-tight text-amber-700">
              {closeData?.pending_deposit.count ?? 0}
            </div>
            <div className="text-xs text-amber-800 mt-1 font-mono">
              {currency(closeData?.pending_deposit.amount || 0)} por cobrar
            </div>
          </div>

          {/* 3. Anticipos recibidos hoy */}
          <div className="border-2 border-emerald-500 p-4 bg-emerald-50/40" data-testid="close-deposits-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-emerald-700">Anticipos hoy</div>
              <Banknote className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="font-display text-4xl mt-3 tracking-tight text-emerald-700">
              {currency(closeData?.deposits_today.amount || 0)}
            </div>
            <div className="text-xs text-emerald-800 mt-1 font-mono">
              {closeData?.deposits_today.count ?? 0} anticipo(s)
            </div>
          </div>

          {/* 4. Finiquitos recibidos hoy */}
          <div className="border-2 border-blue-500 p-4 bg-blue-50/40" data-testid="close-finals-card">
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase text-blue-700">Finiquitos hoy</div>
              <CreditCard className="w-4 h-4 text-blue-600" />
            </div>
            <div className="font-display text-4xl mt-3 tracking-tight text-blue-700">
              {currency(closeData?.finals_today.amount || 0)}
            </div>
            <div className="text-xs text-blue-800 mt-1 font-mono">
              {closeData?.finals_today.count ?? 0} finiquito(s)
            </div>
          </div>
        </div>

        {/* Total received + payment method breakdown */}
        <div className="mt-4 border-2 border-emerald-700 bg-emerald-600 text-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="close-total-card">
          <div>
            <div className="text-xs font-mono uppercase opacity-80">Total ingresado el {closeDate.toLocaleDateString("es-MX")}</div>
            <div className="font-display text-4xl tracking-tight mt-1" data-testid="close-total-received">
              {currency(closeData?.total_received_today || 0)}
            </div>
          </div>
          {/* Breakdown by via */}
          <div className="flex flex-wrap gap-2 text-xs">
            {(() => {
              const merged = {};
              const dep = closeData?.deposits_today.by_via || {};
              const fin = closeData?.finals_today.by_via || {};
              for (const k of Object.keys(dep)) merged[k] = (merged[k] || 0) + dep[k];
              for (const k of Object.keys(fin)) merged[k] = (merged[k] || 0) + fin[k];
              const label = { transferencia: "Transferencia", efectivo: "Efectivo", stripe: "Tarjeta", otro: "Otro" };
              const iconOf = { transferencia: Landmark, efectivo: Banknote, stripe: CreditCard, otro: Wallet };
              const keys = Object.keys(merged);
              if (keys.length === 0) {
                return <span className="italic opacity-80">Sin cobros registrados este día.</span>;
              }
              return keys.map((k) => {
                const Icon = iconOf[k] || Wallet;
                return (
                  <div key={k} className="flex items-center gap-1.5 bg-white/10 border border-white/30 px-2 py-1" data-testid={`close-via-${k}`}>
                    <Icon className="w-3.5 h-3.5" />
                    <span className="font-mono uppercase opacity-90">{label[k] || k}</span>
                    <span className="font-medium">{currency(merged[k])}</span>
                  </div>
                );
              });
            })()}
          </div>
        </div>

        <p className="text-[11px] text-neutral-500 mt-3 leading-snug">
          Después de marcar en <span className="font-mono">Pedidos</span> los checkboxes de <strong>Anticipo</strong> y <strong>Finiquito</strong>, presiona el ícono <RefreshCw className="w-3 h-3 inline" /> aquí para ver el efectivo/transferencias que llevas cobrado en el día. Perfecto para hacer el corte al cierre.
        </p>
      </div>

      {/* Reporte de cobros (exportable) */}
      <div className="brutal-card p-6" data-testid="metrics-payments-report">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-neutral-700" />
            <h3 className="font-display text-xl tracking-tight">Reporte de cobros — Exportar</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-2 border-2 border-neutral-300 hover:bg-neutral-50 text-sm font-medium"
                  data-testid="report-range-btn"
                >
                  <CalendarDays className="w-4 h-4" />
                  {reportRange.from && reportRange.to
                    ? (fmtLocal(reportRange.from) === fmtLocal(reportRange.to)
                        ? reportRange.from.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })
                        : `${reportRange.from.toLocaleDateString("es-MX", { day: "numeric", month: "short" })} – ${reportRange.to.toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}`)
                    : "Elegir rango"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="end">
                <div className="flex flex-wrap gap-1 p-2 border-b border-neutral-200 bg-neutral-50">
                  {[
                    { k: "today", label: "Hoy" },
                    { k: "yesterday", label: "Ayer" },
                    { k: "last7", label: "7 días" },
                    { k: "last30", label: "30 días" },
                    { k: "month", label: "Este mes" },
                    { k: "prevMonth", label: "Mes ant." },
                  ].map((p) => (
                    <button
                      key={p.k}
                      type="button"
                      onClick={() => applyReportPreset(p.k)}
                      className="px-2 py-1 text-[11px] font-mono uppercase border border-neutral-200 bg-white hover:bg-black hover:text-white hover:border-black transition-colors"
                      data-testid={`report-preset-${p.k}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <Calendar
                  mode="range"
                  locale={es}
                  numberOfMonths={2}
                  selected={reportRange}
                  onSelect={(r) => r && setReportRange(r)}
                  defaultMonth={reportRange.from || new Date()}
                  disabled={{ after: new Date() }}
                  data-testid="report-range-calendar"
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              className="rounded-none h-9 border-neutral-400"
              onClick={doExportCsv}
              disabled={reportLoading || reportExporting || !reportData || reportData.rows.length === 0}
              data-testid="report-export-csv"
            >
              <FileDown className="w-4 h-4 mr-2" /> CSV / Excel
            </Button>
            <Button
              className="rounded-none h-9 bg-[#FF3B30] hover:bg-black text-white"
              onClick={doExportPdf}
              disabled={reportLoading || reportExporting || !reportData || reportData.rows.length === 0}
              data-testid="report-export-pdf"
            >
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>

        {/* Summary */}
        {reportLoading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" /></div>
        ) : reportData ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Pedidos con cobros</div>
                <div className="font-display text-2xl mt-1 tracking-tight">{reportData.totals.orders}</div>
              </div>
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Anticipos</div>
                <div className="font-display text-2xl mt-1 tracking-tight text-emerald-700">{currency(reportData.totals.deposit_amount)}</div>
                <div className="text-[10px] text-neutral-500 font-mono">{reportData.totals.deposit_count} cobro(s)</div>
              </div>
              <div className="border border-neutral-200 p-3">
                <div className="text-[10px] font-mono uppercase text-neutral-500">Finiquitos</div>
                <div className="font-display text-2xl mt-1 tracking-tight text-blue-700">{currency(reportData.totals.final_amount)}</div>
                <div className="text-[10px] text-neutral-500 font-mono">{reportData.totals.final_count} cobro(s)</div>
              </div>
              <div className="border-2 border-black p-3 bg-black text-white">
                <div className="text-[10px] font-mono uppercase opacity-80">Total ingresado</div>
                <div className="font-display text-2xl mt-1 tracking-tight" data-testid="report-total-received">{currency(reportData.totals.total_received)}</div>
              </div>
            </div>

            {/* Table preview */}
            {reportData.rows.length === 0 ? (
              <div className="text-sm text-neutral-500 italic py-6 border border-dashed border-neutral-300 text-center">
                No hay cobros de anticipo o finiquito en este rango.
              </div>
            ) : (
              <div className="overflow-auto border border-neutral-200">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-100 text-neutral-600">
                    <tr>
                      <th className="p-2 text-left font-mono uppercase">Pedido</th>
                      <th className="p-2 text-left font-mono uppercase">Cliente</th>
                      <th className="p-2 text-left font-mono uppercase">Teléfono</th>
                      <th className="p-2 text-left font-mono uppercase">Estado</th>
                      <th className="p-2 text-right font-mono uppercase">Anticipo</th>
                      <th className="p-2 text-right font-mono uppercase">Finiquito</th>
                      <th className="p-2 text-right font-mono uppercase">Total</th>
                      <th className="p-2 text-right font-mono uppercase">Ingresado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.slice(0, 12).map((r) => (
                      <tr key={r.order_id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`report-row-${r.order_id}`}>
                        <td className="p-2 font-mono">{r.order_id.replace(/^o_/, "")}</td>
                        <td className="p-2">{r.customer_name}</td>
                        <td className="p-2 font-mono text-neutral-600">{r.customer_phone}</td>
                        <td className="p-2 capitalize">{(r.status || "").replace(/_/g, " ")}</td>
                        <td className="p-2 text-right text-emerald-700 font-mono">
                          {r.deposit_in_range ? currency(r.deposit_amount) : "—"}
                        </td>
                        <td className="p-2 text-right text-blue-700 font-mono">
                          {r.final_in_range ? currency(r.final_amount) : "—"}
                        </td>
                        <td className="p-2 text-right font-mono">{currency(r.total)}</td>
                        <td className="p-2 text-right font-mono font-medium">{currency(r.received_in_range)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reportData.rows.length > 12 && (
                  <div className="p-2 text-[11px] text-neutral-500 bg-neutral-50 border-t border-neutral-100 text-center font-mono">
                    Mostrando 12 de {reportData.rows.length} filas — el archivo exportado incluye todas.
                  </div>
                )}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-3">
              El reporte incluye <strong>solo pedidos con anticipo o finiquito cobrados</strong> en el rango. Elige un día específico (preset Hoy) o un rango de fechas y exporta en <span className="font-mono">CSV</span> (abre en Excel) o <span className="font-mono">PDF</span>.
            </p>
          </>
        ) : (
          <div className="text-sm text-neutral-500 italic">No se pudo cargar el reporte.</div>
        )}
      </div>

      {loading || !data ? (
        <div className="py-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" />
        </div>
      ) : (
        <>
          {/* Revenue trend */}
          <div className="brutal-card p-6">
            <h3 className="font-display text-xl tracking-tight mb-4">Ingresos por {PERIODS.find(p=>p.value===period).label.toLowerCase()}</h3>
            {data.series.length === 0 ? (
              <div className="text-center py-12 text-sm text-neutral-500">Sin datos en el rango</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.series} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#F0F0F0" vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#525252" }} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", color: "#FFF", borderRadius: 0, border: 0, fontSize: 12 }}
                    formatter={(v, k) => (k === "revenue" ? currency(v) : v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="revenue" name="Ingresos (MXN)" stroke="#FF3B30" strokeWidth={2.5} dot={{ r: 3, fill: "#FF3B30" }} />
                  <Line type="monotone" dataKey="orders" name="Pedidos" stroke="#0A0A0A" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Top products */}
          <div className="brutal-card p-6">
            <h3 className="font-display text-xl tracking-tight mb-4">Productos más vendidos</h3>
            {data.top_products.length === 0 ? (
              <div className="text-center py-12 text-sm text-neutral-500">Sin ventas registradas</div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.top_products} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="#F0F0F0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#525252" }} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#0A0A0A", color: "#FFF", borderRadius: 0, border: 0, fontSize: 12 }}
                    formatter={(v, k) => (k === "revenue" ? currency(v) : v)}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                  <Bar dataKey="quantity" name="Piezas" fill="#0A0A0A" />
                  <Bar dataKey="revenue" name="Ingresos (MXN)" fill="#FF3B30" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
